import { Alert, Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { Image } from 'expo-image';
import { Redirect } from 'expo-router';
import { useAuthContext } from '@/components/Auth/AuthContext';
import { Border, Color, FontFamily, FontSize, Padding } from '@/GlobalStyles';
import { Icon, Text } from 'react-native-paper';
import TextInput from '@/components/Form/TextInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useForm } from 'react-hook-form';
import Form from '@/components/Form/Form';
import * as Auth from 'aws-amplify/auth';
import SimpleLink from '@/components/SimpleLink';
import Button from '@/components/Button';
import { useState } from 'react';
import FTextInput from '@/components/Form/TextInput';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

type LoginForm = {
  email: string;
  code: string;
  password: string;
  confirmPassword?: string;
};

const MotdepasseOubliePage = () => {
  const authInfos = useAuthContext();
  const { height, width } = useWindowDimensions();
  const [hasCode, setHasCode] = useState(false);

  const loginForm = useForm<LoginForm>();

  const router = useRouter();
  const { t } = useTranslation();

  const [loginIn, setLoginIn] = useState(false);

  if (authInfos.jwt) {
    return <Redirect href="/patients" />;
  }

  return (
    <View style={[styles.screen, { height }]}>
      <ScrollView
        contentContainerStyle={[styles.loginInner, { paddingHorizontal: 40 }]}
        style={{ flex: 1 }}
      >
        <SafeAreaView style={styles.logoParent}>
          <Pressable
            style={styles.btnBack}
            onPress={() => {
              router.back();
            }}
          >
            <Icon source={'chevron-left'} size={24} color={Color.MainNoir} /><Text>{t('Retour')}</Text>
          </Pressable>
          <Form {...loginForm}>
            <Text style={styles.title}>{t('Mot de passe oublié')}</Text>
            <View style={styles.frameParent}>
              <TextInput
                name="email"
                label={'E-mail'}
                placeholder={'Votre adresse e-mail'}
                validators={[AvailableValidationRules.email, AvailableValidationRules.required]}
              />
              {hasCode ? (
                <>
                  <TextInput
                    name="code"
                    label={'Code'}
                    placeholder={'Votre code'}
                    validators={[AvailableValidationRules.required]}
                  />
                  <FTextInput
                    name={'password'}
                    label={'Nouveau mot de passe'}
                    placeholder={'Votre nouveau mot de passe'}
                    secureTextEntry
                    validators={[
                      {
                        rule: AvailableValidationRules.required,
                        errorMessage: t('Vous devez renseigner votre nouveau mot de passe'),
                        ifNotEmpty: ['motDePasse'],
                      },
                      {
                        rule: AvailableValidationRules.password,
                        errorMessage:
                          t(
                            'Votre mot de passe doit contenir au minimum 8 charactères dont un chiffre, une minuscule, une majuscule et un caractère special de la liste suivante :'
                          ) + ' ^ $ * . [ ] { } ( ) ? - " ! @ # % & / \\ , > < \' : ; | _ ~ ` + =',
                      },
                    ]}
                  />
                  <FTextInput
                    name={'confirmPassword'}
                    label={'Confirmer le mot de passe'}
                    placeholder={'Confirmer votre nouveau mot de passe'}
                    secureTextEntry
                    validators={[
                      {
                        rule: AvailableValidationRules.required,
                        errorMessage: t(
                          'Vous devez confirmer votre nouveau mot de passe pour changer de mot de passe'
                        ),
                        ifNotEmpty: ['password'],
                      },
                      {
                        rule: AvailableValidationRules.equals,
                        errorMessage: t('La confirmation ne correspond pas au mot de passe'),
                        equals: 'password',
                      },
                    ]}
                  />
                </>
              ) : (
                <SimpleLink
                  active={false}
                  style={styles.motDePasseContainer}
                  textStyle={styles.motDePasse}
                  onPress={() => setHasCode(true)}
                >
                  {t('Déjà un code ?')}
                </SimpleLink>
              )}
              <Button
                loading={loginIn}
                disabled={loginIn}
                withShadow={false}
                style={styles.btnForm}
                onPress={loginForm.handleSubmit(async (data) => {
                  setLoginIn(true);
                  try {
                    if (hasCode) {
                      await Auth.confirmResetPassword({
                        username: data.email,
                        confirmationCode: data.code,
                        newPassword: data.password,
                      });
                      await Auth.signIn({ username: data.email, password: data.password });
                      setLoginIn(false);
                      setHasCode(false);
                    } else {
                      await Auth.resetPassword({ username: data.email });
                      setHasCode(true);
                    }
                  } catch (e: any) {
                    Alert.alert('Erreur', e.message);
                  } finally {
                    setLoginIn(false);
                  }
                })}
              >
                {hasCode ? 'Enregistrer' : 'Demander un code'}
              </Button>
            </View>
          </Form>
        </SafeAreaView>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  texteFlexBox: {
    marginTop: 20,
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  loginImage: {
    alignSelf: 'stretch',
    overflow: 'hidden',
  },
  logoIcon: {
    width: 150,
    height: 80,
    alignSelf: 'center',
  },
  motDePasseContainer: {
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
  },
  motDePasse: {
    fontSize: FontSize.p_size,
    lineHeight: 24,
    color: Color.black,
    textAlign: 'right',
    opacity: 0.3,
    marginBottom: 14,
    fontFamily: FontFamily.labelMedium,
    alignSelf: 'stretch',
  },
  iconsmall: {
    width: 22,
    height: 22,
  },
  vousSouhaitezRejoindre: {
    fontSize: FontSize.labelMedium_size,
    lineHeight: 18,
    fontWeight: '500',
    color: Color.MainNoir,
    textAlign: 'center',
    display: 'flex',
    width: 162,
    fontFamily: FontFamily.labelMedium,
    justifyContent: 'center',
    alignItems: 'center',
  },
  texte: {
    paddingHorizontal: 0,
    paddingVertical: Padding.p_11xs,
  },
  infosRapides: {
    padding: Padding.p_11xl,
    borderWidth: 1,
    borderColor: Color.colorGray_100,
    borderRadius: Border.br_3xs,
    marginTop: 35,
    borderStyle: 'solid',
    alignItems: 'center',
    justifyContent: 'center',
  },
  frameParent: {
    marginTop: 50,
  },
  logoParent: {
    maxWidth: 500,
    width: '100%',
  },
  loginInner: {
    paddingVertical: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
  screen: {
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
  },
  title: {
    marginTop: 20,
    fontFamily: FontFamily.labelBold,
    fontSize: FontSize.h3_size,
    color: Color.MainNoir,
    textAlign: 'center',
  },
  btnBack: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  btnForm: {
    marginTop: 20,
  },
});

export default MotdepasseOubliePage;
